/**
 * @jsxRuntime classic
 * @jsx jsx
 */
import React, { useCallback, useState } from 'react';

import { IconButton } from '@atlaskit/button/new';
import { cssMap, cx, jsx } from '@atlaskit/css';
import CrossIcon from '@atlaskit/icon/core/cross';
import LinkIcon from '@atlaskit/icon/core/link';
import { Anchor, Box, Inline, Text } from '@atlaskit/primitives/compiled';
import { token } from '@atlaskit/tokens';

import { normalizeFocusAskLinkUrl } from '../is-allowed-focus-ask-link-url';
import type { FocusAskLinkData } from '../types';

const styles = cssMap({
	linkItem: {
		paddingTop: token('space.075'),
		paddingRight: token('space.100'),
		paddingBottom: token('space.075'),
		paddingLeft: token('space.100'),
	},
	linkItemDivider: {
		borderBottom: `${token('border.width')} solid ${token('color.border')}`,
	},
	linkItemContent: {
		flexGrow: 1,
		minWidth: '0',
	},
	link: {
		color: token('color.text'),
		textDecoration: 'none',
		'&:link': {
			color: token('color.text'),
		},
		'&:visited': {
			color: token('color.text'),
		},
		'&:hover': {
			color: token('color.text'),
			textDecoration: 'underline',
		},
	},
	linkRemoveButton: {
		opacity: 0,
		pointerEvents: 'none',
		transition: 'opacity 0.15s ease-in-out',
		visibility: 'hidden',
	},
	linkRemoveButtonVisible: {
		opacity: 1,
		pointerEvents: 'auto',
		visibility: 'visible',
	},
});

type FocusAskLinkRowBaseProps = {
	link: FocusAskLinkData;
	ariaLabel?: string;
};

type FocusAskLinkRowProps = FocusAskLinkRowBaseProps & {
	canRemove?: boolean;
	onRemove?: () => void;
	removeLabel?: string;
	showDivider?: boolean;
};

export const FocusAskLinkRow = (props: FocusAskLinkRowProps): JSX.Element => {
	const { link, ariaLabel, canRemove = false, onRemove, removeLabel, showDivider = false } = props;
	const [isHovered, setIsHovered] = useState(false);
	const [isFocused, setIsFocused] = useState(false);
	const shouldShowRemoveButton = isHovered || isFocused;
	const shouldRenderRemoveButton = canRemove && onRemove && removeLabel;
	const linkText = link.linkText?.trim() || link.url;
	const safeHref = normalizeFocusAskLinkUrl(link.url);

	const handleBlur = useCallback((event: React.FocusEvent<HTMLElement>) => {
		if (!event.currentTarget.contains(event.relatedTarget)) {
			setIsFocused(false);
		}
	}, []);

	return (
		<Box
			xcss={cx(styles.linkItem, showDivider && styles.linkItemDivider)}
			onMouseEnter={() => setIsHovered(true)}
			onMouseLeave={() => setIsHovered(false)}
			onFocus={() => setIsFocused(true)}
			onBlur={handleBlur}
		>
			<Inline space="space.100" alignBlock="center" spread="space-between">
				<Inline space="space.075" alignBlock="center" xcss={styles.linkItemContent}>
					<LinkIcon label="" size="small" color={token('color.icon')} />
					{safeHref ? (
						<Anchor
							href={safeHref}
							target="_blank"
							rel="noopener noreferrer"
							aria-label={ariaLabel}
							xcss={styles.link}
						>
							{linkText}
						</Anchor>
					) : (
						<Text>{linkText}</Text>
					)}
				</Inline>
				{shouldRenderRemoveButton && (
					<Box
						xcss={cx(
							styles.linkRemoveButton,
							shouldShowRemoveButton && styles.linkRemoveButtonVisible,
						)}
					>
						<IconButton
							appearance="subtle"
							spacing="compact"
							icon={CrossIcon}
							label={removeLabel}
							onClick={onRemove}
						/>
					</Box>
				)}
			</Inline>
		</Box>
	);
};
